import type { CSSProperties, ReactNode } from 'react';
import { Link, NavLink } from 'react-router';
import { useI18n, type Lang } from '../i18n';
import { useSession, useShopEntry } from '../session';

export function Nav() {
  const { t } = useI18n();
  const session = useSession();
  const shopEntry = useShopEntry();
  const link = ({ isActive }: { isActive: boolean }) => `nav-link${isActive ? ' active' : ''}`;
  return (
    <header className="nav">
      <div className="wrap">
        <Link to="/" className="brand" aria-label="落筆成戀 Inked in Love">
          <img className="brand-petal" src="/art/sakura_1.png" alt="" />
          <img className="brand-cn" src="/art/wordmark_cn.webp" alt="" width={640} height={187} />
          <span className="brand-en">
            INKED IN <em>LOVE</em>
          </span>
        </Link>
        <nav className="nav-links">
          <NavLink to="/" end className={link}>
            {t('nav.home')}
          </NavLink>
          {shopEntry ? (
            <NavLink to="/shop" className={link}>
              {t('nav.shop')}
            </NavLink>
          ) : null}
          {/* Signing in is only for the shop: until it opens, that spot asks visitors to pre-register (the app's handoff links still work). */}
          {session ? (
            <NavLink to="/account" className={link}>
              {t('nav.account')}
            </NavLink>
          ) : shopEntry ? (
            <Link to="/login" className="nav-cta">
              {t('nav.login')}
            </Link>
          ) : (
            <Link to="/prereg" className="nav-cta">
              {t('nav.prereg')}
            </Link>
          )}
        </nav>
        <LangToggle />
      </div>
    </header>
  );
}

/** Each language in its own script, so a visitor finds theirs whatever the page is showing. */
const LANG_LABELS: Array<[Lang, string]> = [['zh-HK', '繁'], ['zh-CN', '简'], ['en', 'EN']];

export function LangToggle() {
  const { t, lang, setLang } = useI18n();
  return (
    <select className="lang" value={lang} onChange={(e) => setLang(e.target.value as Lang)} aria-label={t('share.language')}>
      {LANG_LABELS.map(([l, label]) => (
        <option key={l} value={l}>
          {label}
        </option>
      ))}
    </select>
  );
}

/** The brush-lettered 落筆成戀 with "Inked in Love". */
export function Wordmark({ className, style }: { className?: string; style?: CSSProperties }) {
  return <img className={className} style={style} src="/art/wordmark.webp" alt="落筆成戀 Inked in Love" width={640} height={257} />;
}

export function Footer() {
  const { t } = useI18n();
  return (
    <footer className="footer page-in">
      <div className="wrap">
        <Link to="/privacy">{t('footer.privacy')}</Link>
        <span className="sep">|</span>
        <Link to="/terms">{t('footer.terms')}</Link>
        <span className="copy">{t('footer.copy')}</span>
      </div>
    </footer>
  );
}

/** A brush-stroke ink plate: section and page titles. */
export function Plate({ children, large, petal, as: Tag = 'h2', className = '' }: { children: ReactNode; large?: boolean; petal?: boolean; as?: 'h1' | 'h2' | 'h3'; className?: string }) {
  return (
    <Tag className={`plate${large ? ' lg' : ''} ${className}`}>
      {petal ? <img className="petal-mark" src="/art/sakura_1.png" alt="" /> : null}
      {children}
    </Tag>
  );
}

type Spot = CSSProperties & { wide?: boolean };

export function Splat({ n, style }: { n: 1 | 2 | 3; style: Spot }) {
  const { wide, ...css } = style;
  return <img className={`decor${wide ? ' wide-only' : ''}`} src={`/art/splat_${n}.png`} alt="" style={css} />;
}

export function Petal({ n = 1, style }: { n?: 1 | 2; style: Spot }) {
  const { wide, ...css } = style;
  return <img className={`decor${wide ? ' wide-only' : ''}`} src={`/art/sakura_${n}.png`} alt="" style={css} />;
}

export function Halftone({ style }: { style: CSSProperties }) {
  return <div className="halftone wide-only" style={style} aria-hidden />;
}

/** The resource marks the app uses: credits a brush coin, ink coins an ink-drop coin. */
export const CreditIcon = ({ size = 26 }: { size?: number }) => <img className="res-icon" src="/art/coin_1.webp" alt="" width={size} height={size} />;
export const CoinIcon = ({ size = 26 }: { size?: number }) => <img className="res-icon" src="/art/pack_7.webp" alt="" width={size} height={size} />;
